import { useEffect, useState, ReactNode } from "react";
import { format } from "date-fns"
import { Send } from "@mui/icons-material";
import { Box, Typography, Button, Grid, Collapse } from "@mui/material";
import { useStudentContext } from "../../../../context/studentContext";
import { useSlideInContext } from "../../../../context/SlideInContext";
import { NoteOriginType, ParentalConsentType, Student } from "../../../../__generated__/graphql";
import EmailForm, { EmailFormProps, EmailFormSchema } from "../SendToParentOptions/EmailForm";
import { useMutation, useQuery } from "@apollo/client";
import usePersonAuthStore, { decodedToken } from "../../../store/personAuthStore";
import { EditStudentParentEmailMutation, EditStudentParentEmailMutationVariables } from "../../../../__generated__/graphql";
import { EDIT_STUDENT_PARENT_EMAIL } from "../../../../features/graphql/mutation/students/editStudentParentEmailMutation";
import { useManageModal } from "../../../Manage/components/ManageModal";
import { LoadingButton } from "@mui/lab";
import usePersonStore from "../../../store/personStore";
import { STUDENT_SESSIONS_QUERY } from "../../../DashboardPages/components/ResultsTable/ResultsTable";
import { STUDENTS_PAGE_QUERY } from "../../../StudentPages/components/StudentTable";
import { stat } from "fs";
function EmailFormWrapper({ onSubmit }: EmailFormProps) {
    return (
        <Box>
            <Typography>There's no parent email for this child, please provide an email</Typography>
            <EmailForm onSubmit={onSubmit} />
        </Box>
    )
}
function convert_enum_to_strin(input: string) {
    return input
        .toLowerCase()
        .split("_")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join("")
}
type SEND_CONSENT_REQUEST_ACTION = "local" | "redirect" | "notification";
function LaunchButton({ action: type, currentAction: action, onAction, children }: {
    action: SEND_CONSENT_REQUEST_ACTION,
    currentAction: SEND_CONSENT_REQUEST_ACTION | null,
    onAction: (action: SEND_CONSENT_REQUEST_ACTION) => {}, children: ReactNode
}) {
    return (
        <LoadingButton
            sx={{
                backgroundColor: "primary.main",
                "&:hover": {
                    backgroundColor: "primary.light",
                },
                width: 180,
                marginTop: "16px",
                padding: 1
            }}
            type="submit"
            variant="contained"
            loading={type === action}
            loadingPosition="end"
            disabled={Boolean(action)}
            endIcon={<Send />}
            onClick={() => onAction(type)}
        >
            {children}
        </LoadingButton>
    )
}

function ParentalConsent() {
    const { studentData, updateStudent } = useStudentContext()
    const personState = usePersonStore(state => state);
    const { openModal } = useManageModal()
    const { refetch: refetch_students } = useQuery(STUDENTS_PAGE_QUERY, {
        notifyOnNetworkStatusChange: true,
        variables: {
            query: "",
            first: 100
        }
    })
    const { refetch: refetch_sessions } = useQuery(STUDENT_SESSIONS_QUERY, {
        fetchPolicy: 'cache-first',
        variables: {
            studentId: studentData.id
        }
    })
    const [showEmailForm, setShowEmailForm] = useState<boolean>(false);
    const { disableClosingDrawer, toggleDrawer } = useSlideInContext();
    const accessToken = usePersonAuthStore(state => state.access_token)
    const [launchAction, setLaunchAction] = useState<SEND_CONSENT_REQUEST_ACTION | null>(null);
    const [editStudentParentEmail, { data }] = useMutation<EditStudentParentEmailMutation, EditStudentParentEmailMutationVariables>(EDIT_STUDENT_PARENT_EMAIL);
    const handleEmailSubmission = async (values: EmailFormSchema) => {
        await updateStudent({
            parentEmail: values.email
        } as Student);
        const response = await editStudentParentEmail({
            variables: {
                id: studentData.id,
                parentEmail: values.email
            }
        })
        if (response && !response.errors) {
            await handleSendParentalConsent("notification", values.email);
        }

    }
    const origin = window.location.pathname.includes("sessions") ? NoteOriginType.ResultsTable : NoteOriginType.StudentTable
    const handleSendParentalConsent = async (action: SEND_CONSENT_REQUEST_ACTION, updatedEmail?: string) => {
        if (action === "notification" && !updatedEmail) {
            if (studentData.parentEmail == null) {
                setShowEmailForm(true);
                return;
            }
        }
        //PREVENT ACCIDENTAL CLOSING OF THE DRAWER
        setLaunchAction(action)
        disableClosingDrawer(true)
        if (accessToken) {
            let newWindow: Window | null = null;

            if (action == "local") {
                newWindow = window.open("", "_blank");
            }
            const { pid, lid } = decodedToken(accessToken ?? '');
            const response = await fetch(`${window.__env.API_URL}/api/parent/consent/send`, {
                method: "POST",
                headers: {
                    Pid: pid,
                    Lid: lid,
                    "Content-Type": "application/json",
                    authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    action: action,
                    personId: pid,
                    studentId: studentData.id,
                    companyId: personState.company.id,
                    NoteOrigin: convert_enum_to_strin(origin)
                })
            })
            if (response.status >= 400) {
                openModal({ type: "PARENTAL_CONSENT_ERROR", cancelButtonLabel: "close" })
            }
            if (response.status === 200) {
                // const newWindow = window.open("", "_blank"); // MUST be sync
                const json = await response.json();
                if (newWindow) { //just guarding it
                    newWindow.location.href = json.redirectUrl;
                }


                handleSuccessMessage("signNow")
            }

            if (response.status === 202) {
                if (!window.location.pathname.includes("sessions")) {
                    refetch_students()
                } else {
                    refetch_sessions()
                }
                updateStudent({
                    ...studentData,
                    lastConsentAction: {
                        ...studentData.lastConsentAction,
                        createdAt: new Date()
                    },
                    consentEmailSentTimes: studentData.consentEmailSentTimes + 1
                })
                handleSuccessMessage("notification");
                return;
            }
        }
        disableClosingDrawer(false);
        toggleDrawer();
        setLaunchAction(null);
    }
    const handleSuccessMessage = (source: string) => {
        toggleDrawer()
        disableClosingDrawer(false);
        if (source == "notification") {
            openModal({ type: "PARENTAL_CONSENT_SENT", eventType: "DISABLE_BACKDROP", cancelButtonLabel: "Close" })

        } else if (source == "signNow") {
            openModal({ type: "PARENTAL_CONSENT_LOCAL", eventType: "DISABLE_BACKDROP", cancelButtonLabel: "Close" })

        }
    }
    return (
        <Grid container spacing={4} direction='column' alignItems="center" sx={{ padding: "36px 16px", textAlign: "center" }}>
            <Grid size={12} direction='column' alignItems='center'>
                As per your company's policies, you must obtain parental consent before launching any sessions for this student
            </Grid>
            {studentData?.parentalConsentStatus?.toUpperCase() === ParentalConsentType.Pending ?
                <Grid size={12}>
                    <Typography>You last sent a request on {format(new Date(studentData?.lastConsentAction?.createdAt), "MM/dd/yyyy")}</Typography>
                    <Typography>You have sent a request to the parent {studentData?.consentEmailSentTimes} time(s)</Typography>
                </Grid> : <></>
            }
            <Grid size={12}>
                <LaunchButton
                    action="local"
                    currentAction={launchAction}
                    onAction={handleSendParentalConsent}>
                    Sign now
                </LaunchButton>
            </Grid>
            <Grid size={12}>
                <LaunchButton
                    action="notification"
                    currentAction={launchAction}
                    onAction={handleSendParentalConsent}>
                    Send Consent Form to parent
                </LaunchButton>
            </Grid>
            <Collapse in={showEmailForm}>
                <EmailFormWrapper onSubmit={handleEmailSubmission} />
            </Collapse>
            {/* <Grid size={12}>Send a consent form to <b>{studentData.parentEmail}</b></Grid> */}
            <Grid size={12}>
                <Box component="div" sx={{ paddingTop: "16px", display: "flex", justifyContent: "space-between" }}>
                    <Button variant="contained" disableElevation color="inherit" onClick={toggleDrawer}>
                        Cancel
                    </Button>
                </Box>
            </Grid>
        </Grid>
    )
}

export default ParentalConsent

